import { Modal, type ModalFuncProps } from 'antd';
import type { ModalStaticFunctions } from 'antd/es/modal/confirm';
import { Fragment, useId, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useTranslateContent } from '../hooks';
import { ConfirmModalIcon } from './confirm-modal-icon';
import { modalWidths } from './modal-widths';

type CustomModalHookReturn = [CustomModalInstance, React.ReactElement];

// TODO: we only use confirm
type CustomModalInstance = Omit<ModalStaticFunctions, 'warn'>;

type P = ModalFuncProps;

export const useCustomModal = ({ key }: { key?: string } = {}): CustomModalHookReturn => {
  const { t } = useTranslation();
  const translate = useTranslateContent();
  const [modal, contextHolder] = Modal.useModal();
  const uId = useId();
  const customizedModal = useMemo(() => {
    const { confirm: originalConfirm, ...restModal } = modal;

    const createBasicModalContent = ({ cancelText, content, okText, title }: P) => ({
      cancelButtonProps: { type: 'default' as const },
      cancelText: translate(cancelText) ?? t('Cancel'),
      centered: true,
      content: translate(content),
      okText: translate(okText),
      title: translate(title),
      width: modalWidths.small,
    });

    const confirm = ({ cancelText, content, okButtonProps, okText, title, type = 'warning', ...rest }: P) =>
      originalConfirm({
        ...createBasicModalContent({ cancelText, content, okText, title }),
        icon: <ConfirmModalIcon type={type} />,
        okButtonProps: type === 'error' ? { danger: true, ...okButtonProps } : okButtonProps,
        ...rest,
      });

    return {
      ...restModal,
      confirm,
    };
  }, [modal, t, translate]);

  const wrappedContextHolder = useMemo(
    () => <Fragment key={key ?? uId}>{contextHolder}</Fragment>,
    [contextHolder, key, uId],
  );

  return useMemo(() => [customizedModal, wrappedContextHolder], [customizedModal, wrappedContextHolder]);
};
